// PRD 07 §3.2 Text block — title input + WYSIWYG body. Matches the prototype
// `guideBlockHtml(block, 'text')` layout. Owns no state — the parent passes
// the current block and receives partial updates via `onChange`.

import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import type { ContentBlock } from '../types/management-guidelines.types';
import { RichTextEditor } from './RichTextEditor';

interface TextBlockEditorProps {
  block: ContentBlock;
  index: number;
  onChange: (patch: Partial<ContentBlock>) => void;
  onDelete: () => void;
  /** SHCT-582 — PRD 21 §7.4 P-2: locked for a METADATA:READ operator. */
  disabled?: boolean;
}

export function TextBlockEditor({
  block,
  index,
  onChange,
  onDelete,
  disabled = false,
}: TextBlockEditorProps) {
  const { t } = useTranslation('management-guidelines');

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 8,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
          {t('blocks.position', {
            index: index + 1,
            type: t('blocks.types.text'),
          })}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-default btn-secondary"
          disabled={disabled}
          style={{
            height: 28,
            padding: '0 8px',
            color: disabled ? undefined : 'var(--destructive)',
          }}
          title={t('blocks.delete')}
          aria-label={t('blocks.delete')}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <input
        type="text"
        className="form-field__input"
        value={block.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder={t('blocks.titlePlaceholder')}
        disabled={disabled}
        style={{ marginBottom: 6 }}
      />

      <RichTextEditor
        blockId={block.id}
        value={block.text}
        onChange={(html) => onChange({ text: html })}
        placeholder={t('blocks.bodyPlaceholder')}
        readOnly={disabled}
      />
    </div>
  );
}
